import { Component, OnInit, Input, OnDestroy, Output, EventEmitter } from "@angular/core";
import { Store, select } from "@ngrx/store";
import { IAppState } from "@edp-store/state/app.state";
import {
    getSimulation,
    getUploadReading,
    getInvoices,
    getActiveMetersGas,
    getActiveMetersElectricity,
} from "@edp-store/selectors/dashboard.selectors";
import { GetSimulation, GetActiveMeters, SetUploadReading } from "@edp-store/actions/dashboard.actions";
import { Contract } from "@edp-components/edp-contract";
import { ActiveMeters, Simulation } from "../edp-consumptions.models";
import { IInvoice } from "@edp-models/invoices.model";
import { Subject } from "rxjs";
import { takeUntil, withLatestFrom } from "rxjs/operators";
import moment from "moment";
import { IPropertyType } from "@edp-components/edp-contract/edp-contract.model";
import { IReading } from "@edp-models/readings.model";
import { GetCounter } from "@edp-store/actions/user.actions";
import { Router } from "@angular/router";
import { DatalayerService } from "@edp-services/datalayer.service";
import { TranslateService } from "@ngx-translate/core";

@Component({
    selector: "edp-widget-consumptions-simulation",
    templateUrl: "./edp-widget-consumptions-simulation.component.html",
    styleUrls: ["./edp-widget-consumptions-simulation.component.scss"],
})
export class EdpWidgetConsumptionsSimulationComponent implements OnInit, OnDestroy {
    @Input() contract: Contract;
    @Input() activeTab: "E" | "G";
    @Input() hiddenTabActiveMetersSubmitted: ActiveMeters;
    @Input() isMultiMeters: boolean;
    @Output() reloadWidget: EventEmitter<string> = new EventEmitter();
    @Output() executeGetMetersOnDestroy: EventEmitter<{ type: "E" | "G" }> = new EventEmitter();

    private onDestroy$ = new Subject<boolean>();
    public invoices$ = this.store.pipe(select(getInvoices));
    public simulation$ = this.store.pipe(select(getSimulation));

    public activeTabActiveMeters: ActiveMeters = null;
    public isInvoicesAverage: boolean;
    public successUploadReading: boolean;
    public type: IPropertyType;
    public offlineReading = false;
    public offlineReadingNotIndexed = true;
    private isDashboard = false;

    constructor(
        public readonly store: Store<IAppState>,
        public readonly router: Router,
        public datalayerService: DatalayerService,
        public translateService: TranslateService
    ) {
        this.isDashboard = this.router.url.includes("inicio");
    }

    ngOnInit() {
        this.resetValues();
        this.simulation$.pipe(takeUntil(this.onDestroy$), withLatestFrom(this.invoices$)).subscribe(([simulation, invoice]) => {
            if (simulation) {
                this.store.dispatch(new GetActiveMeters(this.contract, this.activeTab));
                this.getActiveTabActiveMeters();
            }
            if (simulation && invoice.invoices) {
                this.isInvoicesAverage = this.getInvoicesAverage(simulation, invoice.invoices);
            }
        });

        this.store.pipe(takeUntil(this.onDestroy$), select(getUploadReading)).subscribe((uploadReading: IReading) => {

            if (uploadReading) {
                this.successUploadReading = true;

                // PLAT-59643 - no execute GetSimulation if contract has indexed offer
                const isTabIndexedOffer =
                    (this.activeTab === "E" && this.contract.HasIndexedOfferE) ||
                    (this.activeTab === "G" && this.contract.HasIndexedOfferG);
                const isTabDynamicOfferE = this.activeTab === "E" && this.contract.HasDynamicOfferE;

                if (uploadReading.success) {
                    this.datalayerSubmitReading();
                    this.executeGetMetersOnDestroy.emit({ type: this.activeTab });

                    if (!this.isMultiMeters) {
                        this.offlineReading = false;
                        this.offlineReadingNotIndexed = !isTabIndexedOffer && !isTabDynamicOfferE;
                        if (this.offlineReadingNotIndexed) {
                            this.store.dispatch(new GetSimulation(this.contract, false));
                        }
                    }

                    if (
                        this.router.routerState.snapshot.url.includes("energia") &&
                        this.router.routerState.snapshot.url.includes("inicio")
                    ) {
                        this.store.dispatch(new GetCounter());
                    }

                } else {
                    if (uploadReading.registersError) {
                        this.datalayerSubmitReadingError();
                        this.reloadWidget.emit(this.activeTab);
                    } else {
                        this.offlineReading = true;
                        this.offlineReadingNotIndexed = !isTabIndexedOffer;
                        this.datalayerSubmitReadingOffline();
                    }
                }
            }
        });

        if (this.hiddenTabActiveMetersSubmitted) {
            this.hasSubmittedActiveMeters();
        }

        this.type = this.contract.getTypeProperties(this.contract.getType());
    }

    protected resetValues(): void {
        this.store.dispatch(new SetUploadReading(null));
        this.offlineReadingNotIndexed = true;
        this.successUploadReading = false;
        this.offlineReading = false;
    }

    private datalayerSubmitReading(): void {
        this.datalayerService.setGaClick("clickNavigation", {
            interactionName: "enviar-leitura",
            interactionLocation: !!this.isDashboard ? "inicio" : "leituras"
        });

        this.datalayerService.setGaEvent("readingsSimulationSubmit", {
            formModal: !!this.isDashboard ? "envio-leitura-inicio" : "envio-leitura",
                serviceAttributes: {
                    mensagemResultado: this.activeTab === "E"
                        ? this.translateService.instant("pt.edp.edponline.readings.page.simulation.loadingDataElectricity")
                        : this.translateService.instant("pt.edp.edponline.readings.page.simulation.loadingDataGas")
                }
            }
        );
    }

    private datalayerSubmitReadingError(): void {
        this.datalayerService.setGaClick("clickError", {
            interactionName: "enviar-leitura",
            interactionLocation: !!this.isDashboard ? "inicio" : "leituras",
            serviceAttributes: {
                campoComErro: "leituras"
            }
        });
    }

    private datalayerSubmitReadingOffline(): void {
        this.datalayerService.setGaEvent("readingsSimulationSubmit", {
            formModal: !!this.isDashboard ? "envio-leitura-inicio" : "envio-leitura",
            serviceAttributes: {
                mensagemResultado: this.translateService.instant("pt.edp.edponline.readings.page.simulation.error.message")
            }
        });
    }

    private getActiveTabActiveMeters(): void {
        if (this.activeTab === "E") {
            this.store.pipe(takeUntil(this.onDestroy$), select(getActiveMetersElectricity)).subscribe((meters: ActiveMeters[]) => {
                if (!!meters && meters.length) {
                    this.activeTabActiveMeters = meters[0];
                }
            });
            return;
        }
        if (this.activeTab === "G") {
            this.store.pipe(takeUntil(this.onDestroy$), select(getActiveMetersGas)).subscribe((meters: ActiveMeters[]) => {
                if (!!meters && meters.length) {
                    this.activeTabActiveMeters = meters[0];
                }
            });
        }
    }

    private hasSubmittedActiveMeters = (): ActiveMeters => {
        let hasSubmittedActiveMeters: ActiveMeters = null;

        if (this.contract.getType() === "dual" && this.hiddenTabActiveMetersSubmitted.lastInserted) {
            const currentDate = moment().startOf("day");
            const lastInsertedDate = moment(this.hiddenTabActiveMetersSubmitted.lastInserted.date, "YYYY-MM-DD");

            if (moment(currentDate).isSame(lastInsertedDate)) {
                hasSubmittedActiveMeters = this.hiddenTabActiveMetersSubmitted;
            }
        }
        return hasSubmittedActiveMeters;
    }

    private getInvoicesAverage = (simulation: Simulation, invoices: IInvoice[]): boolean => {
        const simulationValue: number = Number(simulation.simulationValue.replace(",", ".")) || 0;

        const invoicesAverage: number = this.getAverage(invoices);
        const rangeMaxAverage: number = invoicesAverage + invoicesAverage * 0.2;
        const rangeMinAverage: number = invoicesAverage - invoicesAverage * 0.2;

        return simulationValue > rangeMinAverage && simulationValue < rangeMaxAverage;
    }

    private getAverage = (invoices: IInvoice[]): number => {
        const invoicesValue: number[] = invoices.map(invoice => Number(invoice.invoiceValueEur.replace(",", ".")));
        return invoicesValue.reduce((total: number, score: number) => total + score, 0) / invoicesValue.length;
    }

    public ngOnDestroy() {
        this.onDestroy$.next(true);
        this.onDestroy$.unsubscribe();
    }
}
